import { combineEpics, ofType } from "redux-observable";
import { filter, from, map, mergeMap, skipWhile, switchMap, withLatestFrom } from "rxjs";
import { RootState } from "../../../store/ConfigureStore";
import { setFlopValue, setInflowCalculations, setStaticBottomHolePressurePI, setStaticFluidLevelFromSurfacePI2 } from "../../../store/calculation/calculation-input/WellSlice";
import { getFlopValue, getGetStatFldChange, getInflowCalculations, getReserPrevChange } from "../../../services/calculations/calculation-input/well.service";
import { FLOPObject } from "../../../store/model/calculations/calculation";

const flop = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "well/setCasingPressure",
            "well/setProductivityIndex",
            "well/setStaticBottomHolePressurePI",
            "well/setStaticFluidLevelFromSurfacePI",
            "well/setStaticBottomHolePressureP",
            "well/setFlowingBottomHolePressureP",
            "well/setTestFlowRate",
            "well/setStaticFluidLevelFromSurfaceHead",
            "well/setProducingFluidLevelFromSurfaceHead",
            "well/setTestFlowRateHead",
            "well/setTestFlowRate",
            "well/setDesiredProductionRate",
            "well/setSettingDepth",
            "well/setPumpSettingVerticalDepth",
            "well/setReservoirDatum",
            "fluidTab/setWaterCutCombinedSpGr"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
        ),
        switchMap(([actionData, tab2Input, tab3Input]) => {
            let PI = 0;
            let staticBottomHolePressure = 0;
            let staticFluidLvlFromSurface = 0;
            let FlowRate = 0;
            let flowingBottomHolePress = 0;
            let flowingFluidLvlFromSurface = 0;
            const combinedSpGr = tab3Input?.liquidPhaseCombinedSpGr || 0;

            if (+tab2Input.inflowTypeLstVal == 0) {
                PI = tab2Input.piVal || PI;
                staticBottomHolePressure = tab2Input.reservoirPressure || 0;
                staticFluidLvlFromSurface = tab2Input.staticFluidLvl || 0;
            }
            if (+tab2Input.inflowTypeLstVal == 1) {
                FlowRate = tab2Input.flowRate || 0
                flowingBottomHolePress = tab2Input.intakePressure || 0
                staticBottomHolePressure = tab2Input.sbhpForPressre || 0
            }
            if (+tab2Input.inflowTypeLstVal == 2) {
                FlowRate = tab2Input.flowRateForHd || 0
                flowingFluidLvlFromSurface = tab2Input.prdcngFluidLvl || 0
                staticFluidLvlFromSurface = tab2Input.staticFluidLvlForHd || 0
            }

            var fLOPObject = {
                fBHH: +flowingFluidLvlFromSurface,
                fBHHUnit: +tab2Input.unitPrdcngFluidLvlListVal,
                fBHP: +flowingBottomHolePress,
                fBHPUnit: +tab2Input.unitIntakePressureListVal,
                flowRate: +FlowRate,
                flowRateUnit: +tab2Input.unitFlowRateListVal,
                pI: +PI,
                pIUnit: +tab2Input.unitPIValListVal,
                perforation: +tab2Input.perforation,
                perforationUnit: +tab2Input.unitPerforationListVal,
                productionRate: +tab2Input.productionRate,
                productionRateUnit: +tab2Input.unitProductionRateListVal,
                sBHH: +staticFluidLvlFromSurface,
                sBHHUnit: +tab2Input.unitStaticFluidLvlListVal,
                sBHP: +staticBottomHolePressure,
                sBHPUnit: +tab2Input.unitStaticFluidLvlListVal,
                tVD: +tab2Input.tvd,
                tVDUnit: +tab2Input.unitTVDListVal,
                combinedSpGr: +combinedSpGr || 0,
                inflowType: +tab2Input.inflowTypeLstVal,
                casingPressure: +tab2Input.casingPressure,
                casingPressureUnit: +tab2Input.unitCasingPressureListVal,
                fLOPUnit: +tab2Input.unitFLOPListVal,
                bubblePointPressure: tab3Input?.bubblePoint || 0,
            } as FLOPObject;
            return from(getFlopValue(fLOPObject)).pipe(
                mergeMap(data => {
                    if (data != 0) {
                        return [setFlopValue(Number(data))];
                    }
                    else {
                        return [setFlopValue(null)];
                    }
                }),
            )
        }
        )
    )
};

const fetchStaticFluidLvl = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "well/setCasingPressure",
            "well/setReservoirDatum",
            "fluidTab/setFluidTabDetails",
            "well/setStaticBottomHolePressurePI",
            "fluidTab/setWaterCutCombinedSpGr"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
        ),
        skipWhile(([actionData, tab2Input, tab3Input]) => +tab2Input?.hiddenPresHeadPIIPType === 1),
        switchMap(([actionData, tab2Input, tab3Input]) => {
            const ResvPres = +tab2Input?.reservoirPressure
            const ReserviourDatum = +tab2Input?.perforation

            const pd = {
                ResvPres: (ResvPres || 0),
                CombSpGr: +tab3Input?.combinedSpGr || 0,
                InUnit: +tab2Input?.unitReservoirPressureListVal || 0,
                OutUnit: +tab2Input?.unitStaticFluidLvlListVal,
                Perftn: +(ReserviourDatum || 0),
                PerftnUnit: +tab2Input?.unitPerforationListVal,
                CasingPressure: +tab2Input?.casingPressure || 0,
                CasingPressureUnit: +tab2Input?.unitCasingPressureListVal || 0,
            };

            return from(getReserPrevChange(pd)).pipe(
                map(data => setStaticFluidLevelFromSurfacePI2(Number(data))),
            )
        }
        ))
}

const fetchBottomHolePressure = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "well/setCasingPressure",
            "well/setReservoirDatum",
            "well/setStaticFluidLevelFromSurfacePI",
            "fluidTab/setWaterCutCombinedSpGr"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
        ),
        skipWhile(([actionData, tab2Input, tab3Input]) => +tab2Input.hiddenPresHeadPIIPType === 0),
        switchMap(([actionData, tab2Input, tab3Input]) => {
            const StaticFluidLevelFromSurface = +tab2Input.staticFluidLvl
            const ReserviourDatum = +tab2Input.perforation
            const pd = {
                StacFld: +(StaticFluidLevelFromSurface || 0),
                CombSpGr: +tab3Input.combinedSpGr || 0,
                InUnit: +tab2Input.unitStaticFluidLvlListVal || 0,
                OutUnit: +tab2Input.unitReservoirPressureListVal || 0,
                Perftn: +(ReserviourDatum || 0),
                PerftnUnit: +tab2Input.unitPerforationListVal || 0,
                casingPressure: +tab2Input.casingPressure || 0,
                casingPressureUnit: +tab2Input.unitCasingPressureListVal || 0,
            }

            return from(getGetStatFldChange(pd)).pipe(
                mergeMap(data => {
                    if (data != 0) {
                        return [setStaticBottomHolePressurePI(Number(data))];
                    }
                    else {
                        return [setStaticBottomHolePressurePI(null)];
                    }
                }),
            )
        }),
    )
};

const fetchWellInfoCalculations = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "well/setCasingPressure",
            "well/setProductivityIndex",
            "well/setStaticBottomHolePressurePI",
            "well/setStaticFluidLevelFromSurfacePI2",
            "well/setStaticBottomHolePressureP",
            "well/setFlowingBottomHolePressureP",
            "well/setTestFlowRate",
            "well/setStaticFluidLevelFromSurfaceHead",
            "well/setProducingFluidLevelFromSurfaceHead",
            "well/setTestFlowRateHead",
            "well/setDesiredProductionRate",
            "well/setSettingDepth",
            "well/setPumpSettingVerticalDepth",
            "well/setReservoirDatum",
            "fluidTab/setWaterCutCombinedSpGr"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.tubing?.tab1Input)),
            state$.pipe(map((state: RootState) => state?.wellInput?.tab2Input)),
            state$.pipe(map((state: RootState) => state?.fluid?.tab3Input)),
        ),
        filter(([actionData, tubingDetails, wellDetails, fluidDetails]) =>
            (
                (+wellDetails?.inflowTypeLstVal === 0 && +wellDetails?.piVal > 0 && +wellDetails?.reservoirPressure > 0 && +wellDetails?.staticFluidLvl > 0)
                || (+wellDetails?.inflowTypeLstVal === 1 && +wellDetails?.sbhpForPressre > 0 && +wellDetails?.intakePressure > 0 && +wellDetails?.flowRate > 0)
                || (+wellDetails?.inflowTypeLstVal === 2 && +wellDetails?.staticFluidLvlForHd > 0 && +wellDetails?.prdcngFluidLvl > 0 && +wellDetails?.flowRateForHd > 0)
            )
            && +wellDetails?.productionRate > 0 && +wellDetails?.perforation > 0 && +fluidDetails?.liquidPhaseCombinedSpGr > 0
        ),
        switchMap(([actionData, tubingDetails, wellDetails, fluidDetails]) => {
            let inflowInputModel: object | null = null;
            const inflowType = +(wellDetails?.inflowTypeLstVal) || 0;
            if (inflowType === 0) {
                inflowInputModel = {
                    ProductivityIndex: +(wellDetails?.piVal || 0),
                    ProductivityIndexUnit: +(wellDetails?.unitPIValListVal ?? 0),
                    StaticBottomHolePressure: +(wellDetails?.reservoirPressure ?? 0),
                    StaticBottomHolePressureUnit: +(wellDetails?.unitReservoirPressureListVal ?? 0),
                    StaticFluidLevelFromSurface: +(wellDetails?.staticFluidLvl ?? 0),
                    StaticFluidLevelFromSurfaceUnit: +(wellDetails?.unitStaticFluidLvlListVal ?? 0),
                }
            }
            else if (inflowType === 1) {
                inflowInputModel = {
                    StaticBottomHolePressure: +(wellDetails?.sbhpForPressre ?? 0),
                    StaticBottomHolePressureUnit: +(wellDetails?.unitReservoirPressureListVal ?? 0),
                    FlowingBottomHolePressure: +(wellDetails?.intakePressure ?? 0),
                    FlowingBottomHolePressureUnit: +(wellDetails?.unitIntakePressureListVal ?? 0),
                    FlowRate: +(wellDetails?.flowRate ?? 0),
                    FlowRateUnit: +(wellDetails?.unitFlowRateListVal ?? 0),
                }
            }
            else if (inflowType === 2) {
                inflowInputModel = {
                    StaticFluidLevelFromSurface: +(wellDetails?.staticFluidLvlForHd ?? 0),
                    StaticFluidLevelFromSurfaceUnit: +(wellDetails?.unitStaticFluidLvlListVal ?? 0),
                    ProducingFluidLevelFromSurface: +(wellDetails?.prdcngFluidLvl ?? 0),
                    ProducingFluidLevelFromSurfaceUnit: +(wellDetails?.unitPrdcngFluidLvlListVal ?? 0),
                    FlowRate: +(wellDetails?.flowRateForHd ?? 0),
                    FlowRateUnit: +(wellDetails?.unitFlowRateListVal ?? 0),
                }
            }

            const inflowClaculationInputs = {
                Units: {
                    CasingPressureUnit: +wellDetails?.unitCasingPressureListVal || 0,
                    ProductionRateUnit: +wellDetails?.unitProductionRateListVal || 0,
                    PerforationUnit: +wellDetails?.unitPerforationListVal || 0,
                    ProductivityIndexUnit: +(wellDetails?.unitPIValListVal ?? 0),
                    ProducingFluidLevelFromSurfaceUnit: +(wellDetails?.unitPrdcngFluidLvlListVal ?? 0),
                    StaticBottomHolePressureUnit: +(wellDetails?.unitReservoirPressureListVal ?? 0),
                    StaticFluidLevelFromSurfaceUnit: +(wellDetails?.unitStaticFluidLvlListVal ?? 0),
                    FlowingBottomHolePressureUnit: +(wellDetails?.unitIntakePressureListVal ?? 0),
                    FlowRateUnit: +(wellDetails?.unitFlowRateListVal ?? 0),
                },
                UnitSystem: +tubingDetails?.unitSys,
                InflowType: +wellDetails?.inflowTypeLstVal,
                CasingPressure: +wellDetails?.casingPressure,
                ProductionRate: +wellDetails?.productionRate,
                Perforation: +wellDetails?.perforation,
                LiquidPhaseSpGr: +fluidDetails?.liquidPhaseCombinedSpGr || 0,
                PiInputs: inflowType === 0 ? inflowInputModel : null,
                PressureInflowInputs: inflowType === 1 ? inflowInputModel : null,
                HeadInflowInputs: inflowType === 2 ? inflowInputModel : null
            }

            return from(getInflowCalculations(inflowClaculationInputs)).pipe(
                map(data => setInflowCalculations(data))
            )
        })

    )
};

export const wellEpic = combineEpics(flop, fetchStaticFluidLvl, fetchBottomHolePressure, fetchWellInfoCalculations);